/* <pricing-tabs> — ARIA tablist driven entirely by a live pricing feed.
   With data-live-url set, a skeleton shows while the feed loads, then tabs,
   tables and rows are built from the JSON DTO (v1 storefront pricing: tabs →
   tables → rows in integer minor units). Table photographs are cloned from an
   inert `<template data-table-art="<tab key>/<table key>">` art bank — the
   only merchant-managed part of a table. Any failure (fetch error, non-200,
   timeout, unparseable body, empty/missing tabs, unknown schema_version, or
   an unresolvable {currency} placeholder) reveals the unavailable region.
   The shopper's active currency is appended to the feed URL when it doesn't
   pin one. Hydration runs in the theme editor too, so merchants preview real
   prices. */
if (!customElements.get('pricing-tabs')) {
  const create = (tag, className) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  };

  class PricingTabs extends HTMLElement {
    connectedCallback() {
      this.liveRegion = this.querySelector('[data-live-region]');
      this.fallbackRegion = this.querySelector('[data-fallback]');
      this.skeleton = this.querySelector('.pricing-tabs__skeleton');
      this.tabs = [];
      this.panels = [];

      this.onClick = (event) => this.select(event.currentTarget);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.stampDates();

      if (this.dataset.liveUrl && this.liveRegion && 'fetch' in window) {
        this.loadLive(this.dataset.liveUrl);
      } else {
        this.showFallback();
      }
    }

    disconnectedCallback() {
      this.teardownTabs();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.abortController) this.abortController.abort();
      clearTimeout(this.abortTimer);
    }

    /* ---------- tabs ---------- */

    initTabs(root) {
      this.teardownTabs();
      if (!root) return;
      this.tabs = Array.from(root.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onClick);
        tab.addEventListener('keydown', this.onKeydown);
      });
    }

    teardownTabs() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onClick);
        tab.removeEventListener('keydown', this.onKeydown);
      });
      this.tabs = [];
      this.panels = [];
    }

    select(tab) {
      if (!tab) return;
      this.tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', selected ? 'true' : 'false');
        other.tabIndex = selected ? 0 : -1;
      });
      const panelId = tab.getAttribute('aria-controls');
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== panelId;
      });
    }

    handleKeydown(event) {
      const index = this.tabs.indexOf(event.currentTarget);
      let next = null;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next === null) return;
      event.preventDefault();
      this.tabs[next].focus();
      this.select(this.tabs[next]);
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;

      // Art blocks live outside the panels — the only element that ever carries
      // shopify_attributes — so map "<tab>/<table>" back to its tab.
      const art = event.target.dataset && event.target.dataset.tableArt;
      if (!art) return;
      const tabKey = art.split('/')[0];
      const tab = this.tabs.find((candidate) => candidate.id.endsWith('-tab-' + tabKey));
      if (tab) this.select(tab);
    }

    /* ---------- live feed ---------- */

    /* A {currency} placeholder (legacy, from when the setting was free text —
       a url-type field rejects the braces) resolves to the shopper's active
       currency, or null when that's unknowable so the caller can bail. A URL
       with no currency param at all gets the active currency appended, so
       merchants never have to put one — or a second query param — in the URL. */
    static resolveFeedUrl(url, activeCurrency) {
      if (url.includes('{currency}')) {
        if (!activeCurrency) return null;
        return url.replace('{currency}', activeCurrency);
      }
      if (activeCurrency && !/[?&]currency=/.test(url)) {
        return url + (url.includes('?') ? '&' : '?') + 'currency=' + activeCurrency;
      }
      return url;
    }

    async loadLive(url) {
      const active = window.Shopify && Shopify.currency && Shopify.currency.active;
      url = PricingTabs.resolveFeedUrl(url, active);
      if (url === null) { this.showFallback(); return; } // cannot resolve the placeholder — reveal the unavailable region

      this.setAttribute('aria-busy', 'true');
      if (this.fallbackRegion) this.fallbackRegion.hidden = true;
      this.skeleton.hidden = false;
      this.abortController = new AbortController();
      this.abortTimer = setTimeout(() => this.abortController.abort(), 8000);

      try {
        const response = await fetch(url, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const data = await response.json();
        if (!data || data.schema_version !== 1) throw new Error('unsupported schema_version');
        if (!Array.isArray(data.tabs) || !data.tabs.length) throw new Error('unexpected payload');
        this.hydrate(data);
      } catch (error) {
        this.showFallback();
      } finally {
        clearTimeout(this.abortTimer);
        this.removeAttribute('aria-busy');
      }
    }

    showFallback() {
      this.teardownTabs();
      if (this.skeleton) this.skeleton.hidden = true;
      if (this.liveRegion) {
        this.liveRegion.hidden = true;
        this.liveRegion.textContent = '';
      }
      if (this.fallbackRegion) this.fallbackRegion.hidden = false;
    }

    /* The pack discount is Shopify's price for the product on this page, never a
       figure from the feed: only Shopify knows what this shopper is being charged.
       Returns 0 — meaning "show list prices only" — when there is no pack price, or
       when the feed is quoting a different currency than Shopify is, since
       subtracting across currencies would invent a number. */
    packDiscount(feedCurrency) {
      const amount = Number(this.dataset.packPrice);
      if (!Number.isFinite(amount) || amount <= 0) return 0;
      const active = window.Shopify && Shopify.currency && Shopify.currency.active;
      if (active && feedCurrency && active !== feedCurrency) return 0;
      return amount;
    }

    hydrate(data) {
      const money = this.moneyFormatter(data.currency || 'GBP');
      const pack = this.packDiscount(data.currency);
      const uid = (this.id || 'PricingTabs') + '-live';

      this.liveRegion.textContent = '';
      const bar = create('div', 'pricing-tabs__bar');
      bar.setAttribute('role', 'tablist');
      const heading = this.querySelector('.pricing-tabs__heading');
      if (heading) {
        if (!heading.id) heading.id = uid + '-heading';
        bar.setAttribute('aria-labelledby', heading.id);
      } else {
        bar.setAttribute('aria-label', 'Pricing tabs');
      }
      const panels = [];
      let first = true;

      data.tabs.forEach((tab) => {
        if (!tab || !tab.key || !tab.title) return;

        if (tab.status === 'coming_soon') {
          const chip = create('span', 'pricing-tabs__soon');
          chip.append(tab.title);
          const badge = create('span', 'pricing-tabs__soon-badge');
          badge.textContent = this.dataset.comingSoonLabel || 'Soon';
          chip.append(badge);
          bar.append(chip);
          return;
        }

        const button = create('button', 'pricing-tabs__tab');
        button.type = 'button';
        button.textContent = tab.title;
        button.id = uid + '-tab-' + tab.key;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', uid + '-panel-' + tab.key);
        button.setAttribute('aria-selected', first ? 'true' : 'false');
        if (!first) button.tabIndex = -1;
        bar.append(button);

        const panel = create('div', 'pricing-tabs__panel');
        panel.id = uid + '-panel-' + tab.key;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', button.id);
        panel.hidden = !first;
        panel.append(this.buildPanel(tab, money, pack));
        panels.push(panel);
        first = false;
      });

      this.liveRegion.append(bar);
      panels.forEach((panel) => this.liveRegion.append(panel));

      if (data.as_of) {
        const asOf = new Date(data.as_of);
        if (!Number.isNaN(asOf.getTime())) this.stampDates(asOf);
      }

      this.skeleton.hidden = true;
      if (this.fallbackRegion) this.fallbackRegion.hidden = true;
      this.liveRegion.hidden = false;
      this.initTabs(this.liveRegion);
    }

    buildPanel(tab, money, pack) {
      const fragment = document.createDocumentFragment();
      const tables = Array.isArray(tab.tables) ? tab.tables : [];

      if (tables.length) {
        const wrap = create('div', 'pricing-tabs__tables');
        tables.forEach((table) => wrap.append(this.buildTable(table, money, tab.key, pack)));
        fragment.append(wrap);
      } else {
        const box = create('div', 'pricing-tabs__empty');
        const title = create('span', 'pricing-tabs__empty-title caption-with-letter-spacing');
        title.textContent = tab.title;
        box.append(title);
        const emptyText = this.dataset.emptyTabText;
        if (emptyText) {
          const text = create('span', 'pricing-tabs__empty-text');
          text.textContent = emptyText;
          box.append(text);
        }
        fragment.append(box);
      }

      if (typeof tab.note === 'string' && tab.note) {
        const note = create('p', 'pricing-tabs__tab-note');
        note.textContent = tab.note;
        fragment.append(note);
      }

      return fragment;
    }

    buildTable(table, money, tabKey, pack) {
      const box = create('div', 'pricing-table');

      if (table.metal_name) {
        const head = create('div', 'pricing-table__metal');
        const swatch = create('span', 'pricing-table__swatch pricing-table__swatch--' + (table.tone || 'silver'));
        swatch.setAttribute('aria-hidden', 'true');
        const name = create('span', 'pricing-table__metal-name');
        name.textContent = table.metal_name;
        head.append(swatch, name);
        box.append(head);
      }

      const body = create('div', 'pricing-table__body');
      const art = this.artFor(tabKey, table.key);
      if (art) body.append(art);

      const detail = create('div', 'pricing-table__detail');
      const rows = create('div', 'pricing-table__rows');
      (Array.isArray(table.rows) ? table.rows : []).forEach((row) => {
        if (!row || row.label == null || row.list_price == null) return;
        const line = create('div', 'pricing-table__row');
        const label = create('span', 'pricing-table__label');
        label.textContent = row.label;
        line.append(label);
        // Which rows carry the discount is the feed's call; what the discount is
        // worth is Shopify's. `discounted_price` is the legacy signal and is only
        // read as a flag — its value is deliberately ignored.
        const discountable = table.pack_discount === true || row.discounted_price != null;
        const discounted = discountable && pack > 0 ? row.list_price - pack : null;

        if (discounted != null && discounted > 0) {
          const was = create('s', 'pricing-table__was');
          was.textContent = money(row.list_price);
          const price = create('span', 'pricing-table__price');
          price.textContent = money(discounted);
          const star = create('span', 'pricing-table__star');
          star.textContent = '*';
          price.append(star);
          line.append(was, price);
        } else {
          const price = create('span', 'pricing-table__price');
          price.textContent = money(row.list_price);
          line.append(price);
        }
        rows.append(line);
      });
      detail.append(rows);

      if (table.note && (table.note.heading || table.note.text)) {
        const note = create('div', 'pricing-table__note');
        if (table.note.heading) {
          const heading = create('p', 'pricing-table__note-heading');
          heading.textContent = table.note.heading;
          note.append(heading);
        }
        if (table.note.text) {
          const text = create('p', 'pricing-table__note-text');
          text.textContent = table.note.text;
          note.append(text);
        }
        detail.append(note);
      }

      body.append(detail);
      box.append(body);
      return box;
    }

    /* Art is joined on "<tab key>/<table key>" — table keys repeat across tabs
       (9k-yellow-gold sits under both pendant and necklace), so an unscoped
       lookup hands the wrong photograph to the second one. */
    artFor(tabKey, tableKey) {
      if (!tabKey || !tableKey) return null;
      const template = this.querySelector(
        'template[data-table-art="' + CSS.escape(tabKey + '/' + tableKey) + '"]'
      );
      if (!template) return null;
      const image = template.content.querySelector('img');
      return image ? image.cloneNode(true) : null;
    }

    moneyFormatter(currency) {
      let formatter = null;
      try {
        formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency,
        });
      } catch (error) {
        formatter = null;
      }
      return (minorUnits) => {
        const value = (Number(minorUnits) || 0) / 100;
        return formatter ? formatter.format(value) : currency + ' ' + value.toFixed(2);
      };
    }

    stampDates(date) {
      const now = date || new Date();
      const pad = (value) => String(value).padStart(2, '0');
      const stamp =
        pad(now.getDate()) +
        '/' +
        pad(now.getMonth() + 1) +
        '/' +
        String(now.getFullYear()).slice(2) +
        ' ' +
        pad(now.getHours()) +
        ':' +
        pad(now.getMinutes());
      this.querySelectorAll('[data-price-stamp]').forEach((el) => {
        el.textContent = stamp;
      });
    }
  }

  customElements.define('pricing-tabs', PricingTabs);
}
